import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { BOOK_URL } from "@/lib/booking";

export default function Hero() {
  return (
    <section id="top" className="relative overflow-hidden pt-8 pb-24 lg:pb-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="h-[1px] w-12 bg-[#0B1215]" />
              <span className="font-mono-label text-[#495057]">
                <MapPin size={12} className="inline mr-2 -mt-0.5" />
                Warwickshire · & surrounding areas
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#0B1215] leading-[0.95] tracking-tighter"
              data-testid="hero-heading"
            >
              One trade.<br />
              <span className="italic font-light">Every</span> property.<br />
              Properly done.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="mt-8 text-lg text-[#495057] max-w-xl leading-relaxed"
            >
              A qualified property handyman specialising in end-of-tenancy deep cleans, move-in
              prep and around-the-home repairs. Trusted by landlords, letting agents, tenants
              <span className="text-[#0B1215] font-medium"> and private homeowners</span> across Warwickshire.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <a
                href={BOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#FF5A36] hover:bg-[#E04928] text-[#F8F9FA] px-8 py-4 font-mono-label active:scale-95 transition-all sharp-shadow"
                data-testid="hero-cta-quote"
              >
                Book a slot <ArrowRight size={16} />
              </a>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 border-2 border-[#0B1215] text-[#0B1215] px-8 py-4 font-mono-label hover:bg-[#0B1215] hover:text-[#F8F9FA] active:scale-95 transition-all"
                data-testid="hero-cta-services"
              >
                See services
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-14 grid grid-cols-3 gap-6 max-w-md"
            >
              {[
                { n: "Trusted", l: "Client base" },
                { n: "24h", l: "Typical turnaround" },
                { n: "4.8★", l: "Google rating" },
              ].map((s) => (
                <div key={s.l} className="border-t-2 border-[#0B1215] pt-3">
                  <div className="font-display text-2xl sm:text-3xl">{s.n}</div>
                  <div className="font-mono-label text-[#495057] mt-1">{s.l}</div>
                </div>
              ))}
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative aspect-[4/5] overflow-hidden bg-[#0B1215]"
            >
              <img
                src="https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=940&w=940"
                alt="Pristine, professionally prepared property interior"
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute top-6 left-6 bg-[#F8F9FA] px-4 py-2 font-mono-label">
                · NEW VENTURE · YEARS OF GRAFT
              </div>
              <div className="absolute bottom-6 right-6 bg-[#FF5A36] text-[#F8F9FA] px-4 py-3 font-mono-label max-w-[200px] leading-tight">
                Honest graft.<br />No agency markup.
              </div>
            </motion.div>
            <div className="hidden lg:block absolute -bottom-8 -left-8 w-32 h-32 border-2 border-[#0B1215] -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
}
